import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout.jsx';
import AdminAuthAPI from '../../api/admin/auth';
import './AdminSyncMonitoring.css';

// Get correct API base URL
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  } else {
    return '/api';
  }
};

/**
 * Admin Sync Monitoring Dashboard
 * Multi-tenant database metrics monitoring with health status and critical alerts
 */
const AdminSyncMonitoring = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'tenant_name', direction: 'asc' });

  useEffect(() => {
    loadMonitoringData();
  }, []);

  const loadMonitoringData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${getApiBaseUrl()}/admin/sync/monitoring/overview`, {
        headers: {
          'Authorization': `Bearer ${AdminAuthAPI.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load monitoring data: ${response.status}`);
      }

      const responseData = await response.json();
      setData(responseData);
    } catch (err) {
      console.error('Error loading monitoring data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadMonitoringData();
  };

  const handleViewMetrics = (tenantId) => {
    navigate(`/admin/sync/monitoring/${tenantId}`);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter and sort tenants
  const getFilteredAndSortedTenants = () => {
    if (!data?.tenants) return [];

    let filtered = data.tenants;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tenant =>
        tenant.tenant_name.toLowerCase().includes(query) ||
        tenant.schema_name.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(tenant => tenant.status === statusFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null values
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      // Convert to comparable values
      if (sortConfig.key === 'record_count') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      } else if (sortConfig.key === 'last_sync_time') {
        aValue = new Date(aValue).getTime() || 0;
        bValue = new Date(bValue).getTime() || 0;
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status) => {
    const badges = {
      healthy: { class: 'badge-healthy', label: 'Healthy' },
      warning: { class: 'badge-warning', label: 'Warning' },
      error: { class: 'badge-error', label: 'Error' }
    };
    const badge = badges[status] || badges.healthy;
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  const filteredTenants = getFilteredAndSortedTenants();

  if (loading && !data) {
    return (
      <AdminLayout>
        <div className="sync-monitoring-container">
          <div className="loading-state">Loading monitoring data...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="sync-monitoring-container">
        <div className="monitoring-header">
          <div className="header-content">
            <h1>Sync Monitoring Dashboard</h1>
            <p className="header-subtitle">Multi-tenant database metrics and sync health monitoring</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn-refresh"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="error-alert">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* System Summary Cards */}
        {data?.system_summary && (
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-label">Total Tenants</div>
              <div className="card-value">{formatNumber(data.system_summary.total_tenants)}</div>
            </div>
            <div className="summary-card">
              <div className="card-label">Tenants with Errors</div>
              <div className="card-value error">{formatNumber(data.system_summary.tenants_with_errors)}</div>
            </div>
            <div className="summary-card">
              <div className="card-label">Total Records</div>
              <div className="card-value">{formatNumber(data.system_summary.total_records)}</div>
            </div>
            <div className="summary-card">
              <div className="card-label">Last Sync</div>
              <div className="card-value small">{formatDateTime(data.system_summary.last_sync_time)}</div>
            </div>
          </div>
        )}

        {/* Critical Alerts Section */}
        {data?.tenants && (
          <div className="critical-alerts-section">
            <h2>Critical Alerts</h2>
            {data.tenants.filter(t => t.critical_alerts && t.critical_alerts.length > 0).length > 0 ? (
              <div className="alerts-list">
                {data.tenants
                  .filter(t => t.critical_alerts && t.critical_alerts.length > 0)
                  .map(tenant => (
                    <div key={tenant.tenant_id} className="alert-item">
                      <div className="alert-header">
                        <span className="alert-tenant">{tenant.tenant_name}</span>
                        {getStatusBadge(tenant.status)}
                      </div>
                      <ul className="alert-messages">
                        {tenant.critical_alerts.map((alert, idx) => (
                          <li key={idx}>{alert}</li>
                        ))}
                      </ul>
                      <button
                        onClick={() => handleViewMetrics(tenant.tenant_id)}
                        className="btn-view-details"
                      >
                        View Details
                      </button>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="no-alerts">No critical alerts - all tenants healthy!</div>
            )}
          </div>
        )}

        {/* Filters and Search */}
        <div className="filters-section">
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Status</option>
            <option value="healthy">Healthy</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
        </div>

        {/* Tenants Table */}
        <div className="tenants-section">
          <h2>Tenants ({filteredTenants.length})</h2>
          {filteredTenants.length > 0 ? (
            <div className="table-container">
              <table className="tenants-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('tenant_name')} className="sortable">
                      Tenant Name {sortConfig.key === 'tenant_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('status')} className="sortable">
                      Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('last_sync_time')} className="sortable">
                      Last Sync {sortConfig.key === 'last_sync_time' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('record_count')} className="sortable">
                      Records {sortConfig.key === 'record_count' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.map(tenant => (
                    <tr key={tenant.tenant_id}>
                      <td>
                        <div className="tenant-name">{tenant.tenant_name}</div>
                        <div className="tenant-schema">{tenant.schema_name}</div>
                      </td>
                      <td>{getStatusBadge(tenant.status)}</td>
                      <td>{formatDateTime(tenant.last_sync_time)}</td>
                      <td className="record-count">{formatNumber(tenant.record_count)}</td>
                      <td>
                        <button
                          onClick={() => handleViewMetrics(tenant.tenant_id)}
                          className="btn-view-metrics"
                        >
                          View Metrics
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">No tenants found matching your filters.</div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSyncMonitoring;
